import {
  Control,
  FieldError,
  FieldErrors,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form'
import {
  DataGridPro,
  GridColDef,
  GridRenderEditCellParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  useTheme,
  createTheme,
  ThemeProvider,
} from '@mui/material'
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import {
  CreateExamFormValues,
  EANMClass,
  PosologyPatientType,
  PosologySliceType,
} from '@services/dtos'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { validatePosologySlices } from '@utils/validatePosologySlices'
import {
  PosologyPatientTypeLabels,
  PosologySliceTypeLabels,
} from '@utils/constants'

const EMPTY_SLICE = {
  patientType: PosologyPatientType.ADULT,
  type: PosologySliceType.MBQ_FIXED,
  activityMBq: 0,
  weightMin: null,
  weightMax: null,
  eanmClass: EANMClass.A,
  eanmBaseActivityMBq: 0,
  eanmMinActivityMBq: 0,
}

const POSOLOGY_PATIENT_TYPE_OPTIONS = (
  Object.entries(PosologyPatientTypeLabels) as Array<
    [PosologyPatientType, string]
  >
).map(([value, label]) => ({ label, value }))
const POSOLOGY_TYPE_OPTIONS = (
  Object.entries(PosologySliceTypeLabels) as Array<[PosologySliceType, string]>
).map(([value, label]) => ({ label, value }))

const PositiveNumberInput = ({
  api,
  id,
  field,
  value,
}: GridRenderEditCellParams) => {
  const [localValue, setLocalValue] = useState(value?.toString() ?? '')

  const commit = (raw: string) => {
    if (raw === '') {
      api.setEditCellValue({ id, field, value: null })
      return
    }
    const num = parseFloat(parseFloat(raw).toFixed(2))
    if (isNaN(num) || num < 0) {
      return
    }
    api.setEditCellValue({ id, field, value: num })
  }

  return (
    <TextField
      size="small"
      type="number"
      variant="standard"
      InputProps={{ disableUnderline: true }}
      value={localValue}
      inputProps={{ min: 0, step: 0.01 }}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => commit(localValue)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit(localValue)
        }
        if (e.key === '-') {
          e.preventDefault()
        }
      }}
      sx={{
        '& input': { padding: '4px 8px', fontSize: '0.75rem' },
      }}
    />
  )
}

interface PosologyGridProps {
  compoundIndex: number
  control: Control<CreateExamFormValues>
  errors: FieldErrors<CreateExamFormValues>
  watch: UseFormWatch<CreateExamFormValues>
}

export function PosologyGrid({
  compoundIndex,
  control,
  errors,
  watch,
}: PosologyGridProps) {
  const parentTheme = useTheme()
  const theme = createTheme(parentTheme, {
    components: {
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
          },
        },
      },
    },
  })
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: `examCompounds.${compoundIndex}.posologySlices`,
  })
  const intl = useIntl()

  const rows = fields.map((field, i) => ({ ...field, _index: i }))
  const slices = watch(`examCompounds.${compoundIndex}.posologySlices`) ?? []
  const invalidIndices = useMemo(() => validatePosologySlices(slices), [slices])

  const columns: GridColDef[] = [
    {
      field: 'patientType',
      headerName: 'Patient',
      width: 95,
      editable: true,
      type: 'singleSelect',
      valueOptions: POSOLOGY_PATIENT_TYPE_OPTIONS,
      valueFormatter: ({ value }) =>
        PosologyPatientTypeLabels[value] ?? 'Inconnu',
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 95,
      editable: true,
      type: 'singleSelect',
      valueOptions: ({ row }) =>
        row.patientType === PosologyPatientType.ADULT
          ? POSOLOGY_TYPE_OPTIONS.filter(
              ({ value }) => value !== PosologySliceType.EANM,
            )
          : POSOLOGY_TYPE_OPTIONS,
      valueFormatter: ({ value }) =>
        PosologySliceTypeLabels[value] ?? 'Inconnu',
    },
    {
      field: 'weightMin',
      headerName: 'Poids min',
      width: 90,
      editable: true,
      renderCell: ({ value, row }) =>
        row.type === PosologySliceType.EANM ? (
          <span className="text-gray-300">—</span>
        ) : value != null ? (
          `≥ ${value} kg`
        ) : (
          '—'
        ),
      renderEditCell: (params) => <PositiveNumberInput {...params} />,
    },
    {
      field: 'weightMax',
      headerName: 'Poids max',
      width: 90,
      editable: true,
      renderCell: ({ value, row }) =>
        row.type === PosologySliceType.EANM ? (
          <span className="text-gray-300">—</span>
        ) : value != null ? (
          `< ${value} kg`
        ) : (
          '—'
        ),
      renderEditCell: (params) => <PositiveNumberInput {...params} />,
    },
    {
      field: 'activityMBq',
      headerName: 'Activité',
      width: 95,
      editable: true,
      renderCell: ({ value, row }) => {
        if (row.type === PosologySliceType.EANM) {
          return <span className="text-gray-300">—</span>
        }
        return row.type === PosologySliceType.MBQ_PER_KG
          ? `${value} MBq/kg`
          : `${value} MBq`
      },
      renderEditCell: (params) => <PositiveNumberInput {...params} />,
    },
    {
      field: 'eanmClass',
      headerName: 'Classe EANM',
      width: 110,
      editable: true,
      type: 'singleSelect',
      valueOptions: Object.values(EANMClass).map((value) => ({
        value,
        label: value,
      })),
      renderCell: ({ value, row }) =>
        row.type !== PosologySliceType.EANM ? (
          <span className="text-gray-300">—</span>
        ) : (
          value ?? '—'
        ),
    },
    {
      field: 'eanmBaseActivityMBq',
      headerName: 'Base activité',
      width: 110,
      editable: true,
      renderCell: ({ value, row }) =>
        row.type !== PosologySliceType.EANM ? (
          <span className="text-gray-300">—</span>
        ) : value != null ? (
          `${value} MBq`
        ) : (
          '—'
        ),
      renderEditCell: (params) => <PositiveNumberInput {...params} />,
    },
    {
      field: 'eanmMinActivityMBq',
      headerName: 'Activité min',
      width: 105,
      editable: true,
      renderCell: ({ value, row }) =>
        row.type !== PosologySliceType.EANM ? (
          <span className="text-gray-300">—</span>
        ) : value != null ? (
          `${value} MBq`
        ) : (
          '—'
        ),
      renderEditCell: (params) => <PositiveNumberInput {...params} />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: ({ row }) => (
        <IconButton size="small" onClick={() => remove(row._index)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ]

  const apiRef = useGridApiRef()

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ mt: 2 }}>
        {rows.map((_, i) => {
          const sliceErrors =
            errors?.examCompounds?.[compoundIndex]?.posologySlices?.[i]
          if (!sliceErrors) {
            return null
          }
          const { weightMax: _ignored, ...rest } = sliceErrors
          const messages = Object.values(rest)
            .map((err) => (err as FieldError)?.message)
            .filter(Boolean) as string[]
          if (!messages.length) {
            return null
          }
          return (
            <Typography key={i} variant="caption" color="error" display="block">
              {`${i + 1} : ${messages
                .map((id) => intl.formatMessage({ id }))
                .join('. ')}`}
            </Typography>
          )
        })}
        <DataGridPro
          getRowId={(row) => row._index}
          rows={rows}
          columns={columns}
          apiRef={apiRef}
          onCellClick={(params, event) => {
            if (params.isEditable) {
              const cellMode = apiRef.current.getCellMode(
                params.id,
                params.field,
              )
              if (cellMode === 'view') {
                apiRef.current.startCellEditMode({
                  id: params.id,
                  field: params.field,
                })
              }
            }
          }}
          sx={{
            '& .MuiDataGrid-editInputCell': {
              fontSize: '0.875rem !important',
            },
            '& .MuiDataGrid-editInputCell .MuiSelect-select': {
              fontSize: '0.875rem !important',
              padding: '2px 8px',
            },
            '& .MuiDataGrid-cell--editing .MuiSelect-select': {
              fontSize: '0.875rem !important',
              padding: '2px 8px',
            },
            '& .MuiDataGrid-cell--editable': {
              position: 'relative',
              cursor: 'text',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '6px',
                right: '6px',
                height: '2px',
                backgroundColor: 'rgba(53, 58, 62, 0.35)',
                borderRadius: '1px',
              },
            },
            '& .MuiDataGrid-row:nth-of-type(even)': {
              backgroundColor: 'white',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: '#e91e620d',
              },
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#e91e620d',
            },
          }}
          experimentalFeatures={{ newEditingApi: true }}
          processRowUpdate={(newRow, oldRow) => {
            const patientTypeChanged = newRow.patientType !== oldRow.patientType
            let type = newRow.type
            if (
              patientTypeChanged &&
              newRow.patientType === PosologyPatientType.CHILD
            ) {
              type = PosologySliceType.EANM
            }
            if (
              patientTypeChanged &&
              newRow.patientType !== PosologyPatientType.CHILD &&
              type === PosologySliceType.EANM
            ) {
              type = PosologySliceType.MBQ_FIXED
            }
            const isEanm = type === PosologySliceType.EANM
            const updated = {
              patientType: newRow.patientType,
              type,
              weightMin: isEanm ? null : newRow.weightMin ?? null,
              weightMax: isEanm ? null : newRow.weightMax ?? null,
              activityMBq: isEanm ? null : newRow.activityMBq ?? 0,
              eanmClass: isEanm ? newRow.eanmClass : null,
              eanmBaseActivityMBq: isEanm ? newRow.eanmBaseActivityMBq : null,
              eanmMinActivityMBq: isEanm ? newRow.eanmMinActivityMBq : null,
            }
            update(newRow._index, updated)
            return { ...newRow, ...updated }
          }}
          isCellEditable={({ field, row }) => {
            if (
              field === 'eanmClass' ||
              field === 'eanmBaseActivityMBq' ||
              field === 'eanmMinActivityMBq'
            ) {
              return row.type === PosologySliceType.EANM
            }
            if (
              field === 'weightMin' ||
              field === 'weightMax' ||
              field === 'activityMBq'
            ) {
              return row.type !== PosologySliceType.EANM
            }
            return true
          }}
          onProcessRowUpdateError={(error) => {
            console.log('PosologyGrid onProcessRowUpdateError', error)
          }}
          getRowClassName={({ row }) =>
            invalidIndices.has(row._index)
              ? '!bg-red-100 hover:!bg-red-200 !border-l-2 !border-red-500'
              : ''
          }
          hideFooter
          disableColumnMenu
          autoHeight
          density="compact"
        />
        {errors?.examCompounds?.[compoundIndex]?.posologySlices &&
          (
            errors.examCompounds[compoundIndex]
              .posologySlices as unknown as FieldError
          )?.message && (
            <Typography variant="caption" color="error" display="block">
              {intl.formatMessage({
                id: (
                  errors.examCompounds[compoundIndex]
                    .posologySlices as unknown as FieldError
                ).message,
              })}
            </Typography>
          )}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => append(EMPTY_SLICE)}
          sx={{ mt: 1 }}
        >
          Ajouter une tranche
        </Button>
      </Box>
    </ThemeProvider>
  )
}
